import Vue from 'vue';
import VueRouter from 'vue-router';
import routes from './routes';
import {useAuthStore} from '@store';
import {
    afterEachRoute,
    beforeEachRoute,
    isRequestProxyByV2,
    matchCommonIntegrationsRoute,
    pageRedirect,
    paramsFromQueryString,
    paramsToQueryString,
    toggleSSPALoader,
    unloadAppAndSiblings
} from '@helpers/router-helper';
import {debugf} from '@helpers/string';
import {thirdPartyService} from "@packages/integrations/service";
import {trim} from "lodash";
import { isSeller } from '@helpers/user';
Vue.use(VueRouter);

const router = new VueRouter({
    mode: 'history',
    routes,
});

router.beforeEach(async (to, from, next) => {
    
    if (await thirdPartyService.redirectToWebhook()) return;

    let toNamePath = trim(to.name || to.path, " \r\n\t\\/");

    debugf('beforeEachRoute/ #integrations/ #1', {to: toNamePath});

    var invitationToken = to.query.token || to.query.tokenSms;

    const storeAuth = useAuthStore();

    let uuid = storeAuth.me?.uuid,
        loggedIn = Boolean(storeAuth.loggedIn) && !!uuid,
        authRequiredPageList = ['partner-grant', 'partner-listings-new', 'partner-listings', 'partner-v2-login'],
        authRequired = authRequiredPageList.includes(toNamePath),
        refreshPlatformList = authRequired || (storeAuth._thirdPartyPlatformList || []).length === 0,
        routeQuery = paramsFromQueryString((from && !!from.name ? from : to).query),
        fromPlatform = Boolean(routeQuery.referringPlatform) ? routeQuery.referringPlatform : null,
        thirdPartyUuid = storeAuth.thirdPartyUuid,
        accessToken = null;

    if (fromPlatform) {
        //test referrer for older version v2 domain
        let proxyByV2 = isRequestProxyByV2(routeQuery),
            resource = (await thirdPartyService.getActiveThirdPartyPlatformList(refreshPlatformList)).find(p => p?.slug == fromPlatform) || {},
            authCallbackUrl = routeQuery.authCallbackUrl ? `${decodeURIComponent(routeQuery.authCallbackUrl)}` : '',
            accessNotRevoked = true;

        accessToken = fromPlatform && !!routeQuery.accessToken ? routeQuery.accessToken + '' : null;

        debugf('beforeEachRoute/ #integrations/ #2', {authRequired, fromPlatform: resource.slug, accessToken});

        if (resource?.uuid && resource?.clientUuid) {
            debugf('beforeEachRoute/ #integrations/ #3');
            await storeAuth.setThirdPartyUuid(resource?.uuid);
        }

        if (authRequired) {
            if (accessToken) {
                try {
                    accessNotRevoked = await storeAuth.thirdPartyLogin(
                        fromPlatform,
                        accessToken
                    );

                    uuid = storeAuth.me?.uuid;
                    loggedIn = Boolean(storeAuth.loggedIn) && !!uuid;
                    debugf('beforeEachRoute/ #integrations/ #4', {loggedIn, uuid, accessNotRevoked});
                } catch (e) {
                    uuid = null;
                    loggedIn = false;
                }
            }

            delete to.query.accessToken;
            delete routeQuery.accessToken;

            if (loggedIn) {
                // we got the access token correct & we could log in user
                if (!accessNotRevoked) {
                    // but it was revoked & we need to double-check if the user allows access
                    if (window.location.pathname.indexOf('/grant') === -1) {
                        debugf('beforeEachRoute/ #integrations/ #grant@router', {accessNotRevoked});

                        if (!routeQuery.authCallbackUrl && to.path.indexOf('grant') === -1) {
                            // downside is if 3rd party didn't supply the authCallbackUrl to register the new access token, or they don't save it after the initial process
                            // we at least send them to the required page if they authorize the action
                            routeQuery.authCallbackUrl = window.env.APP_URL + to.path + '?' + paramsToQueryString(routeQuery);
                        }

                        if (Boolean(storeAuth.me?.email)) {
                            // force user to enter password & confirming his identity before continuing with partner grant
                            let dataParam = {
                                referringPlatform: fromPlatform,
                                redirectUrl: window.env.APP_URL + '/partner/grant?email=@&' + paramsToQueryString(routeQuery),
                                email: storeAuth.me?.email,
                            };

                            await storeAuth.logout();

                            return await pageRedirect(
                                window.env.APP_URL + '/auth?',
                                paramsToQueryString(dataParam),
                                false
                            );
                        } else {
                            // weird case - actually this part should never happen... it's just a fallback
                            return await pageRedirect(
                                window.env.APP_URL + '/partner/grant?email=@&',
                                paramsToQueryString(routeQuery)
                            );
                        }
                    }
                } else if(window.location.pathname.indexOf('/grant') !== -1 && !routeQuery.authCallbackUrl) {
                    // we don't have necessary callback url & we have already a valid access token -> treat it like a normal login
                    return await pageRedirect(window.env.APP_URL + '/dashboard');
                }

            } else {
                // we didn't get a valid access token
                debugf('beforeEachRoute/ #integrations/ #auth');

                let dataParam = {
                    email: routeQuery.email || '',
                    state: routeQuery.state || '',
                    firstName: routeQuery.firstName || '',
                    lastName: routeQuery.lastName || '',
                    licenseId: routeQuery.licenseId || '',
                    nrdsId: routeQuery.nrdsId || ''
                };

                return await pageRedirect(
                    window.env.APP_URL + '/auth?',
                    paramsToQueryString({
                        type: 'agent',
                        referringPlatform: fromPlatform,
                        ...Object.entries(dataParam).reduce((a,[k,v]) => (v ? (a[k]=v, a) : a), {}),
                        redirectUrl: window.env.APP_URL + (routeQuery.authCallbackUrl ? '/partner/grant?email=@&' : to.path + '?') + paramsToQueryString(routeQuery)
                    }),
                    false
                );
            }
        }
    } else if (!loggedIn && Boolean(thirdPartyUuid)) {
        debugf('beforeEachRoute/ #integrations/ #5');
        await storeAuth.setThirdPartyUuid('');
    }
    else if(loggedIn && invitationToken && Boolean(to.path.indexOf('auth') > -1) && isSeller(storeAuth.me)){
        debugf('beforeEachRoute/ #integrations/ #6 Inviation Token Found and Redirecting to Order Linkage Register');
        return await pageRedirect(window.env.APP_URL + '/seller/orders/token-link-account/' + invitationToken);
    }

    if (Boolean(to.path) && to.path !== from.path && Boolean(router.resolve(to.path)?.route?.name)) {
        let advance = await beforeEachRoute(
            to, from, next, null, !fromPlatform && !thirdPartyUuid || !refreshPlatformList, true
        );

        if (advance) {
            debugf('beforeEachRoute/ #integrations/ #next', {
                path: to.path,
                query: routeQuery
            });

            toggleSSPALoader('off');
            return accessToken
                ? await pageRedirect(
                    window.env.APP_URL + to.path + '?',
                    paramsToQueryString(routeQuery)
                )
                : next();
        } else {
            debugf(['beforeEachRoute/ #integrations/ #check route - no follow', advance, to.path]);
        }
    }

    if (window.SSPA) {
        if ((window.SSPA.getMountedApps() || []).length > 1) {
            toggleSSPALoader('off');
        }

        //force unload module app
        await unloadAppAndSiblings(
            'integrations',
            {
                mountable: false,
                disabled: !(fromPlatform && !thirdPartyUuid) && !matchCommonIntegrationsRoute(),
                mountableOther: '',
                disabledOther: ''
            }
        );
    }
});
router.afterEach(afterEachRoute);

export default router;
